import type { ListSignatureRunsResponse } from "@repo/marketplace-admin"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import { RotateCcw } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import { PageHeader, Toolbar } from "@/components/layout"
import { Button } from "@/components/ui/button"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import {
	Instant,
	MODEL_TYPE_LABELS,
	type ModelType,
	QueryError,
	StatusBadge,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/signature-runs")({
	component: SignatureRunsPage,
})

const ALL = "__all__"
const RUN_STATUSES = ["queued", "running", "done", "failed", "superseded"]
const RUN_KINDS = ["card_signatures", "set_symbol_signatures"]

type RunRow = ListSignatureRunsResponse["runs"][number]

function SignatureRunsPage() {
	const allowed = useScannerGate()
	const { orpc, client, isAuthReady } = useMarketplaceOrpc()
	const queryClient = useQueryClient()

	const [statusFilter, setStatusFilter] = React.useState(ALL)
	const [kindFilter, setKindFilter] = React.useState(ALL)

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.listSignatureRuns.queryOptions({
			input: {
				status:
					statusFilter === ALL
						? undefined
						: (statusFilter as
								| "queued"
								| "running"
								| "done"
								| "failed"
								| "superseded"),
				kind:
					kindFilter === ALL
						? undefined
						: (kindFilter as "card_signatures" | "set_symbol_signatures"),
			},
		}),
		enabled: allowed && isAuthReady,
		refetchInterval: 15_000,
	})

	// Resolve model ids to "Embedder v2" style labels for the grid.
	const { data: modelsData } = useQuery({
		...orpc.cardScanner.listModels.queryOptions({ input: {} }),
		enabled: allowed && isAuthReady,
	})
	const modelLabels = React.useMemo(() => {
		const map = new Map<string, string>()
		for (const model of modelsData?.models ?? []) {
			map.set(
				model.id,
				`${MODEL_TYPE_LABELS[model.type as ModelType] ?? model.type} v${model.version}`,
			)
		}
		return map
	}, [modelsData])

	const rerunMutation = useMutation({
		mutationFn: (run: RunRow) =>
			client.cardScanner.triggerSignatureRun({
				gameSlug: run.gameSlug,
				kind: run.kind,
				modelId: run.modelId,
			}),
		onSuccess: ({ run }) => {
			toast.success(`Re-run enqueued for ${run.gameSlug}`)
			queryClient.invalidateQueries({
				queryKey: orpc.cardScanner.listSignatureRuns.key(),
			})
		},
		onError: (error) => toast.error(error.message),
	})

	const columnDefs = React.useMemo<ColDef<RunRow>[]>(
		() => [
			{
				headerName: "Game",
				field: "gameSlug",
				minWidth: 140,
				cellClass: "font-mono",
			},
			{
				headerName: "Model",
				field: "modelId",
				minWidth: 150,
				valueFormatter: (p) =>
					modelLabels.get(p.value as string) ??
					`${String(p.value).slice(0, 8)}…`,
			},
			{
				headerName: "Kind",
				field: "kind",
				minWidth: 130,
				valueFormatter: (p) =>
					p.value === "card_signatures" ? "cards" : "set symbols",
			},
			{
				headerName: "Status",
				field: "status",
				minWidth: 130,
				cellRenderer: (p: ICellRendererParams<RunRow>) => {
					if (!p.data) return null
					const badge = <StatusBadge status={p.data.status} />
					if (p.data.status === "failed" && p.data.failureReason) {
						return (
							<Tooltip>
								<TooltipTrigger asChild>
									<span>{badge}</span>
								</TooltipTrigger>
								<TooltipContent>{p.data.failureReason}</TooltipContent>
							</Tooltip>
						)
					}
					return badge
				},
			},
			{
				headerName: "DB v",
				field: "dbVersion",
				minWidth: 80,
				type: "rightAligned",
				cellClass: "tabular-nums",
				valueFormatter: (p) => (p.value == null ? "—" : String(p.value)),
			},
			{
				headerName: "Trigger",
				field: "trigger",
				minWidth: 140,
				valueFormatter: (p) => String(p.value).replaceAll("_", " "),
			},
			{
				headerName: "Attempt",
				field: "attempt",
				minWidth: 90,
				type: "rightAligned",
				cellClass: "tabular-nums",
				valueFormatter: (p) => (p.value > 1 ? `#${p.value}` : ""),
			},
			{
				headerName: "Created",
				field: "createdAt",
				sort: "desc",
				minWidth: 140,
				cellRenderer: (p: ICellRendererParams<RunRow>) =>
					p.data ? <Instant iso={p.data.createdAt} /> : null,
			},
			{
				headerName: "Finished",
				field: "finishedAt",
				minWidth: 140,
				cellRenderer: (p: ICellRendererParams<RunRow>) =>
					p.data ? <Instant iso={p.data.finishedAt} /> : null,
			},
			{
				headerName: "",
				colId: "actions",
				minWidth: 70,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<RunRow>) =>
					p.data ? (
						<span data-no-row-click>
							<Button
								variant="ghost"
								size="icon"
								title="Re-run this game and model"
								aria-label="Re-run signature generation"
								disabled={rerunMutation.isPending}
								onClick={() => p.data && rerunMutation.mutate(p.data)}
							>
								<RotateCcw className="h-4 w-4" />
							</Button>
						</span>
					) : null,
			},
		],
		[modelLabels, rerunMutation],
	)

	if (!allowed) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Signature runs"
				count={data?.runs.length}
				description="Per-game signature database generation, one run per game and embedder version"
			/>

			<Toolbar>
				<Select value={kindFilter} onValueChange={setKindFilter}>
					<SelectTrigger className="w-56">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>All kinds</SelectItem>
						{RUN_KINDS.map((kind) => (
							<SelectItem key={kind} value={kind}>
								{kind.replaceAll("_", " ")}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-44">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>All statuses</SelectItem>
						{RUN_STATUSES.map((status) => (
							<SelectItem key={status} value={status}>
								{status}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Toolbar>

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : isError ? (
				<QueryError what="signature runs" error={error} />
			) : data?.runs.length === 0 ? (
				<p className="text-muted-foreground">
					No signature runs. Runs appear when a catalog change fans out to live
					embedders, or when you prepare a model on the Models page.
				</p>
			) : (
				<ScannerGrid<RunRow>
					columnDefs={columnDefs}
					rowData={data?.runs}
					getRowId={(row) => row.id}
				/>
			)}
		</div>
	)
}
